import React, { useState, useEffect } from 'react';

const AdminPanel = () => {
  // These are like memory boxes that remember stuff
  const [isAuthenticated, setIsAuthenticated] = useState(false); // Are we logged in?
  const [adminKey, setAdminKey] = useState(''); // The password box
  const [activeCodes, setActiveCodes] = useState([]); // List of all codes
  const [usageLog, setUsageLog] = useState([]); // Who used what codes
  const [newCode, setNewCode] = useState(''); // New code we want to add
  const [loading, setLoading] = useState(false); // Are we waiting for something?
  const [message, setMessage] = useState(''); // Messages to show the user

  // This makes messages disappear after 5 seconds (like Snapchat!)
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // This makes a random code like "BTLXYZ123ABC"
  const generateRandomCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = 'BTL'; // Start with BTL
    for (let i = 0; i < 9; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  // This checks if your password is correct
  const handleAdminLogin = async () => {
    if (!adminKey.trim()) {
      setMessage('Please enter admin key');
      return;
    }

    setLoading(true); // Show "loading..." 
    try {
      // Ask our security guard (from Step 1) if password is right
      const response = await fetch('/api/admin-verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ adminKey: adminKey.trim() })
      });

      const data = await response.json();

      if (response.ok) {
        setIsAuthenticated(true); // Let them in!
        setMessage('✅ Welcome to BTL Calculator Admin Panel');
        await loadDashboardData(); // Load all the data
      } else {
        setMessage(`❌ ${data.error}`);
        setAdminKey(''); // Clear the password box
      }
    } catch (error) {
      console.error('Login error:', error);
      setMessage('❌ Connection error. Please try again.');
    }
    setLoading(false); // Stop showing "loading..."
  };

  // This gets all the data for our dashboard
  const loadDashboardData = async () => {
    try {
      // Ask both our workers for information at the same time
      const [codesResponse, usageResponse] = await Promise.all([
        fetch('/api/admin-codes'), // Ask code counter
        fetch('/api/admin-usage')  // Ask detective
      ]);

      if (codesResponse.ok) {
        const codesData = await codesResponse.json();
        setActiveCodes(codesData.codes || []);
      }

      if (usageResponse.ok) {
        const usageData = await usageResponse.json();
        setUsageLog(usageData.registrations || []);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setMessage('❌ Error loading dashboard data');
    }
  };

  // This adds a new invite code
  const handleAddCode = async () => {
    if (!newCode.trim()) {
      setMessage('❌ Please enter a code');
      return;
    }

    setLoading(true);
    try {
      // Ask our code maker to add the new code
      const response = await fetch('/api/admin-add-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          code: newCode.trim().toUpperCase(), 
          adminKey 
        })
      });

      const data = await response.json();

      if (response.ok) {
        setMessage(`✅ ${data.message}`);
        setNewCode(''); // Clear the text box
      } else {
        setMessage(`❌ ${data.error}`);
      }
    } catch (error) {
      console.error('Add code error:', error);
      setMessage('❌ Error adding code');
    }
    setLoading(false);
  };

  // This removes an invite code
  const handleRemoveCode = async (code) => {
    // Ask "Are you sure?" like when you delete photos
    if (!window.confirm(`Are you sure you want to remove invite code "${code}"?`)) {
      return;
    }

    setLoading(true);
    try {
      // Ask our code remover to delete it
      const response = await fetch('/api/admin-remove-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, adminKey })
      });

      const data = await response.json();

      if (response.ok) {
        setMessage(`✅ ${data.message}`);
      } else {
        setMessage(`❌ ${data.error}`);
      }
    } catch (error) {
      console.error('Remove code error:', error);
      setMessage('❌ Error removing code');
    }
    setLoading(false);
  };

  // This lets you press Enter instead of clicking login
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading) {
      handleAdminLogin();
    }
  };

  // This lets you press Enter to add a code
  const handleAddCodeKeyPress = (e) => {
    if (e.key === 'Enter' && !loading && newCode.trim()) {
      handleAddCode();
    }
  };

  // IF YOU'RE NOT LOGGED IN, SHOW THE LOGIN SCREEN
  if (!isAuthenticated) {
    return (
      <div style={{ 
        minHeight: '100vh', // Full screen height
        background: 'linear-gradient(135deg, #1e3c72, #2a5298)', // Pretty blue background
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Arial, sans-serif',
        padding: '20px'
      }}>
        <div style={{
          backgroundColor: 'white', // White box in the middle
          padding: '40px',
          borderRadius: '10px', // Rounded corners
          boxShadow: '0 10px 25px rgba(0,0,0,0.2)', // Drop shadow
          width: '100%',
          maxWidth: '400px'
        }}>
          {/* The title */}
          <div style={{ textAlign: 'center', marginBottom: '30px' }}>
            <h1 style={{ color: '#2a5298', margin: '0 0 10px 0', fontSize: '28px' }}>
              BTL Calculator
            </h1>
            <h2 style={{ color: '#666', margin: '0', fontWeight: 'normal', fontSize: '18px' }}>
              Admin Panel
            </h2>
          </div>

          {/* Password input box */}
          <div style={{ marginBottom: '20px' }}>
            <input
              type="password" // Hides what you type with dots
              value={adminKey}
              onChange={(e) => setAdminKey(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter admin key"
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #ddd',
                borderRadius: '5px',
                fontSize: '16px',
                boxSizing: 'border-box',
                opacity: loading ? 0.7 : 1 // Fade when loading
              }}
            />
          </div>

          {/* Login button */}
          <button
            onClick={handleAdminLogin}
            disabled={loading || !adminKey.trim()}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: loading || !adminKey.trim() ? '#ccc' : '#2a5298',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              fontSize: '16px',
              cursor: loading || !adminKey.trim() ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.3s'
            }}
          >
            {loading ? 'Authenticating...' : 'Access Admin Panel'}
          </button>

          {/* Messages (success or error) */}
          {message && (
            <div style={{
              marginTop: '15px',
              padding: '12px',
              backgroundColor: message.includes('❌') ? '#ffebee' : '#e8f5e8',
              color: message.includes('❌') ? '#c62828' : '#2e7d32',
              borderRadius: '5px',
              textAlign: 'center',
              fontSize: '14px'
            }}>
              {message}
            </div>
          )}
        </div>
      </div>
    );
  }

  // IF YOU ARE LOGGED IN, SHOW THE ADMIN DASHBOARD
  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: '#f5f5f5', // Light gray background
      fontFamily: 'Arial, sans-serif'
    }}>
      {/* Top header bar */}
      <div style={{
        backgroundColor: '#2a5298', // Blue header
        color: 'white',
        padding: '20px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{ 
          maxWidth: '1200px', 
          margin: '0 auto', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '10px'
        }}>
          <h1 style={{ margin: 0, fontSize: '24px' }}>BTL Calculator - Admin Panel</h1>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {/* Refresh button */}
            <button
              onClick={loadDashboardData}
              disabled={loading}
              style={{
                backgroundColor: 'transparent',
                color: 'white',
                border: '1px solid white',
                padding: '8px 16px',
                borderRadius: '5px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '14px'
              }}
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
            {/* Logout button */}
            <button
              onClick={() => {
                setIsAuthenticated(false);
                setAdminKey('');
                setActiveCodes([]);
                setUsageLog([]);
                setMessage('');
              }}
              style={{
                backgroundColor: 'transparent',
                color: 'white',
                border: '1px solid white',
                padding: '8px 16px',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
        {/* Message bar at top */}
        {message && (
          <div style={{
            marginBottom: '20px',
            padding: '15px',
            backgroundColor: message.includes('❌') ? '#ffebee' : '#e8f5e8',
            color: message.includes('❌') ? '#c62828' : '#2e7d32',
            borderRadius: '8px',
            border: `1px solid ${message.includes('❌') ? '#ffcdd2' : '#c8e6c9'}`,
            fontSize: '14px'
          }}>
            {message}
          </div>
        )}

        {/* Statistics cards (like iPhone widgets) */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
          gap: '20px', 
          marginBottom: '30px' 
        }}>
          {/* Card 1: Active codes */}
          <div style={{
            backgroundColor: 'white',
            padding: '25px',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <h3 style={{ margin: '0 0 10px 0', color: '#2a5298', fontSize: '18px' }}>
              Active Invite Codes
            </h3>
            <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#4caf50' }}>
              {activeCodes.length}
            </div>
          </div>

          {/* Card 2: Registrations */}
          <div style={{
            backgroundColor: 'white',
            padding: '25px',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <h3 style={{ margin: '0 0 10px 0', color: '#2a5298', fontSize: '18px' }}>
              Total Registrations
            </h3>
            <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#ff9800' }}>
              {usageLog.length}
            </div>
          </div>
        </div>

        {/* Add new code section */}
        <div style={{
          backgroundColor: 'white',
          padding: '25px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginBottom: '30px'
        }}>
          <h2 style={{ margin: '0 0 20px 0', color: '#2a5298', fontSize: '20px' }}>
            Add New Invite Code
          </h2>
          
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            {/* Text input for new code */}
            <div style={{ flex: '1', minWidth: '200px' }}>
              <input
                type="text"
                value={newCode}
                onChange={(e) => setNewCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                onKeyPress={handleAddCodeKeyPress}
                placeholder="Enter invite code (e.g., BROKER2024)"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #ddd',
                  borderRadius: '5px',
                  fontSize: '16px',
                  fontFamily: 'monospace', // Computer-style font
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            {/* Generate random button */}
            <button
              onClick={() => setNewCode(generateRandomCode())}
              disabled={loading}
              style={{
                padding: '12px 20px',
                backgroundColor: loading ? '#ccc' : '#4caf50',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                whiteSpace: 'nowrap'
              }}
            >
              Generate Random
            </button>
            
            {/* Add code button */}
            <button
              onClick={handleAddCode}
              disabled={loading || !newCode.trim()}
              style={{
                padding: '12px 20px',
                backgroundColor: loading || !newCode.trim() ? '#ccc' : '#2a5298',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: loading || !newCode.trim() ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                whiteSpace: 'nowrap'
              }}
            >
              {loading ? 'Adding...' : 'Add Code'}
            </button>
          </div>

          <p style={{ 
            margin: '15px 0 0 0', 
            color: '#666', 
            fontSize: '13px',
            fontStyle: 'italic'
          }}>
            💡 After adding a code here, you'll need to manually add it to your Vercel settings
          </p>
        </div>

        {/* Show all active codes */}
        <div style={{
          backgroundColor: 'white',
          padding: '25px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginBottom: '30px'
        }}>
          <h2 style={{ margin: '0 0 20px 0', color: '#2a5298', fontSize: '20px' }}>
            Active Invite Codes ({activeCodes.length})
          </h2>
          
          {activeCodes.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#666',
              fontStyle: 'italic'
            }}>
              <p>No active invite codes found</p>
              <p style={{ fontSize: '14px', margin: '10px 0 0 0' }}>
                Add codes to your Vercel settings first: INVITE_CODE_1, INVITE_CODE_2, etc.
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '12px' }}>
              {activeCodes.map((codeInfo, index) => (
                <div key={index} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '15px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e9ecef'
                }}>
                  <div>
                    <span style={{ 
                      fontFamily: 'monospace', 
                      fontSize: '18px', 
                      fontWeight: 'bold',
                      color: '#2a5298'
                    }}>
                      {codeInfo.code}
                    </span>
                    <div style={{
                      fontSize: '12px',
                      color: '#666',
                      marginTop: '4px'
                    }}>
                      Environment: {codeInfo.envVar}
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveCode(codeInfo.code)}
                    disabled={loading}
                    style={{
                      padding: '8px 15px',
                      backgroundColor: loading ? '#ccc' : '#f44336',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Usage log (who used codes) */}
        <div style={{
          backgroundColor: 'white',
          padding: '25px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ margin: '0 0 20px 0', color: '#2a5298', fontSize: '20px' }}>
            Who Used Which Codes
          </h2>
          
          {usageLog.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#666',
              fontStyle: 'italic'
            }}>
              <p>No one has used codes yet</p>
              <p style={{ fontSize: '14px', margin: '10px 0 0 0' }}>
                When people register, you'll see them here
              </p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                fontSize: '14px'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ 
                      padding: '12px', 
                      textAlign: 'left', 
                      borderBottom: '2px solid #dee2e6',
                      color: '#2a5298'
                    }}>
                      Invite Code
                    </th>
                    <th style={{ 
                      padding: '12px', 
                      textAlign: 'left', 
                      borderBottom: '2px solid #dee2e6',
                      color: '#2a5298'
                    }}>
                      Email Address
                    </th>
                    <th style={{ 
                      padding: '12px', 
                      textAlign: 'left', 
                      borderBottom: '2px solid #dee2e6',
                      color: '#2a5298'
                    }}>
                      When They Joined
                    </th>
                    <th style={{ 
                      padding: '12px', 
                      textAlign: 'left', 
                      borderBottom: '2px solid #dee2e6',
                      color: '#2a5298'
                    }}>
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {usageLog.map((entry, index) => (
                    <tr key={index} style={{
                      borderBottom: '1px solid #dee2e6'
                    }}>
                      <td style={{ 
                        padding: '12px', 
                        fontFamily: 'monospace', 
                        fontWeight: 'bold',
                        color: '#2a5298'
                      }}>
                        {entry.inviteCode}
                      </td>
                      <td style={{ 
                        padding: '12px',
                        color: '#333'
                      }}>
                        {entry.email}
                      </td>
                      <td style={{ 
                        padding: '12px',
                        color: '#666'
                      }}>
{entry.registrationDate} at {entry.registrationTime}                      </td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
  padding: '4px 12px',
  borderRadius: '12px',
  fontSize: '12px',
  fontWeight: 'bold',
  backgroundColor: '#e8f5e8',
  color: '#2e7d32'
}}>
  Active
</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;